import { ORDER_TAB_ALGO } from '@/features/order/order-form/hooks/use-order-tab-lock'
import { offersAlgo, type VenueCaps } from '@/lib/configs/venue-capabilities'
import { OrderFormSummaryTab } from '@/state/order'
import { AlgoType } from '@/types/generated/algo'
import { OrderType } from '@/types/generated/common/trading'

const algoRequirement = (tab: OrderFormSummaryTab) => (c: VenueCaps) =>
  offersAlgo(c, ORDER_TAB_ALGO[tab])

const PRIMARY_TABS = [OrderFormSummaryTab.Limit, OrderFormSummaryTab.Market]
const DROPDOWN_TABS = [
  OrderFormSummaryTab.TWAP,
  OrderFormSummaryTab.AdvancedTwap,
  OrderFormSummaryTab.LimitChase,
  OrderFormSummaryTab.Scale,
  OrderFormSummaryTab.RFQ,
  OrderFormSummaryTab.SingleHedge,
] as const

// What the card's venues must offer for a tab to be shown. A tab the venue can't place is
// hidden outright (no upgrade hint); plan locks apply only to tabs the venue supports.
export const ORDER_TAB_REQUIREMENT: Record<OrderFormSummaryTab, (c: VenueCaps) => boolean> = {
  [OrderFormSummaryTab.Market]: (c) => c.orderTypes.includes(OrderType.ORDER_TYPE_MARKET),
  [OrderFormSummaryTab.Limit]: (c) => c.orderTypes.includes(OrderType.ORDER_TYPE_LIMIT),
  [OrderFormSummaryTab.RFQ]: (c) => c.rfq,
  [OrderFormSummaryTab.TWAP]: algoRequirement(OrderFormSummaryTab.TWAP),
  [OrderFormSummaryTab.AdvancedTwap]: algoRequirement(OrderFormSummaryTab.AdvancedTwap),
  [OrderFormSummaryTab.LimitChase]: algoRequirement(OrderFormSummaryTab.LimitChase),
  [OrderFormSummaryTab.Scale]: algoRequirement(OrderFormSummaryTab.Scale),
  [OrderFormSummaryTab.SingleHedge]: algoRequirement(OrderFormSummaryTab.SingleHedge),
}

/** Fill the second primary slot with the first supported advanced order type. */
export function resolveOrderTabs(caps: VenueCaps) {
  const primary = PRIMARY_TABS.filter((tab) => ORDER_TAB_REQUIREMENT[tab](caps))
  const dropdown = DROPDOWN_TABS.filter((tab) => ORDER_TAB_REQUIREMENT[tab](caps))
  if (primary.length === 1 && primary[0] === OrderFormSummaryTab.Market && dropdown.length > 0) {
    primary.push(dropdown.shift()!)
  }
  return {
    primary,
    dropdown,
    spreader: offersAlgo(caps, AlgoType.ALGO_TYPE_SPREADER),
  }
}

/**
 * The tab to show: the selected one, unless the server says the card's venues can't place it,
 * then Market (or Limit when Market is out too). Basic order types are known on fallback
 * caps; saved algo tabs wait for server capabilities to avoid losing a supported selection.
 */
export function snapTab(
  selected: OrderFormSummaryTab,
  caps: VenueCaps,
  fromServer: boolean
): OrderFormSummaryTab {
  if ((!fromServer && !PRIMARY_TABS.includes(selected)) || ORDER_TAB_REQUIREMENT[selected](caps))
    return selected
  return ORDER_TAB_REQUIREMENT[OrderFormSummaryTab.Market](caps)
    ? OrderFormSummaryTab.Market
    : OrderFormSummaryTab.Limit
}
